// Cobro en 3 renglones fijos: efectivo, tarjeta y transferencia, cada uno con monto, candado y
// «todo aquí». Al cambiar un monto la diferencia se reparte entre los renglones libres.
import { useId, useState, type ReactNode } from 'react';
import { ArrowUpRight, Banknote, Check, CreditCard, Lock, Unlock, Zap } from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';
import { cn } from '@/lib/cn';
import { formatMoneyCents } from '@/shared/lib/format/money';
import { receiptErrors } from '../salePayments';
import {
    assignAll,
    canTakeAmount,
    cardDraft,
    setBlockAmount,
    speiDraft,
    toggleLock,
    type FixedPaymentRows,
    type RowKey,
} from '../fixedPaymentRows';
import { CardSplitsModal } from './CardSplitsModal';
import {
    ERROR_TEXT,
    IconButton,
    RECEIPT_INPUT,
    RowAmount,
    sanitize,
} from './FixedPaymentRowsParts';

const NOOP = (): void => undefined;

const TITLES: Record<RowKey, string> = {
    cash: 'Efectivo',
    card: 'Tarjeta',
    spei: 'Transferencia',
};

export interface FixedPaymentRowsFormProps {
    totalCents: number;
    rows: FixedPaymentRows;
    onChange?: (next: FixedPaymentRows) => void;
    /** Cobro ya registrado: solo se ven los renglones con monto. */
    readOnly?: boolean;
}

export function FixedPaymentRowsForm({
    totalCents,
    rows,
    onChange = NOOP,
    readOnly = false,
}: FixedPaymentRowsFormProps) {
    const cashOkId = useId();
    const [cardModalOpen, setCardModalOpen] = useState(false);
    const cardCount = rows.card.cards.length;
    const cardErrors = rows.card.cards.some(
        (c) => Object.keys(receiptErrors(cardDraft(c))).length > 0,
    );
    const speiErrors = receiptErrors(speiDraft(rows));

    /** Un renglón en $0 sin otro libre con saldo no tiene de dónde tomar monto. */
    const isAmountDisabled = (key: RowKey): boolean => !canTakeAmount(rows, key, totalCents);

    const rowProps = (key: RowKey) => ({
        title: TITLES[key],
        readOnly,
        active: rows[key].amountCents > 0,
        locked: rows[key].locked,
        cents: rows[key].amountCents,
        onCommit: (cents: number) => {
            onChange(setBlockAmount(rows, key, cents, totalCents));
        },
        onToggleLock: () => {
            onChange(toggleLock(rows, key));
        },
        onAssignAll: () => {
            onChange(assignAll(rows, key, totalCents));
        },
    });

    const show = (key: RowKey): boolean => !readOnly || rows[key].amountCents > 0;

    return (
        <div className="flex flex-col gap-2">
            {show('cash') && (
                <MethodRow
                    {...rowProps('cash')}
                    icon={<Banknote className="size-5" aria-hidden />}
                    amountDisabled={isAmountDisabled('cash')}
                    detail={
                        readOnly ? (
                            rows.cash.cashOk ? (
                                <span className="text-[12px] text-muted-foreground">
                                    Confirmado en la caja
                                </span>
                            ) : null
                        ) : (
                            <div className="flex items-start gap-2">
                                <Checkbox
                                    id={cashOkId}
                                    checked={rows.cash.cashOk}
                                    onCheckedChange={(checked) => {
                                        onChange({
                                            ...rows,
                                            cash: { ...rows.cash, cashOk: checked === true },
                                        });
                                    }}
                                    className="mt-0.5"
                                />
                                <label
                                    htmlFor={cashOkId}
                                    className="cursor-pointer text-[12px] font-medium leading-[1.35]"
                                >
                                    Confirmo que el cliente pagó{' '}
                                    <span className="tabular-nums">
                                        {formatMoneyCents(rows.cash.amountCents)}
                                    </span>{' '}
                                    en la caja
                                </label>
                            </div>
                        )
                    }
                />
            )}

            {show('card') && (
                <MethodRow
                    {...rowProps('card')}
                    icon={<CreditCard className="size-5" aria-hidden />}
                    amountDisabled={isAmountDisabled('card') || cardCount > 1}
                    detail={
                        <CardDetailButton
                            rows={rows}
                            readOnly={readOnly}
                            hasErrors={cardErrors}
                            onOpen={() => {
                                setCardModalOpen(true);
                            }}
                        />
                    }
                    footer={
                        cardErrors && !readOnly ? (
                            <p className={ERROR_TEXT}>
                                Revisa el comprobante de la tarjeta o deja el campo vacío.
                            </p>
                        ) : null
                    }
                />
            )}

            {show('spei') && (
                <MethodRow
                    {...rowProps('spei')}
                    icon={<ArrowUpRight className="size-5" aria-hidden />}
                    amountDisabled={isAmountDisabled('spei')}
                    detail={
                        readOnly ? (
                            <span className="font-mono text-[12px] text-muted-foreground">
                                {[rows.spei.speiKey, rows.spei.senderClabe]
                                    .filter(Boolean)
                                    .join(' · ')}
                            </span>
                        ) : (
                            <div className="flex flex-wrap items-center gap-2">
                                <input
                                    type="text"
                                    autoComplete="off"
                                    maxLength={30}
                                    placeholder="Clave de rastreo (opcional)"
                                    value={rows.spei.speiKey}
                                    aria-label="Clave de rastreo de la transferencia"
                                    aria-invalid={speiErrors.speiKey ? true : undefined}
                                    onChange={(e) => {
                                        onChange({
                                            ...rows,
                                            spei: {
                                                ...rows.spei,
                                                speiKey: sanitize(
                                                    e.target.value,
                                                    /[^A-Za-z0-9]/g,
                                                    30,
                                                ),
                                            },
                                        });
                                    }}
                                    className={cn(RECEIPT_INPUT, 'w-[200px] font-mono')}
                                />
                                <input
                                    type="text"
                                    inputMode="numeric"
                                    autoComplete="off"
                                    maxLength={18}
                                    placeholder="CLABE ordenante (opcional)"
                                    value={rows.spei.senderClabe}
                                    aria-label="CLABE ordenante de la transferencia"
                                    aria-invalid={speiErrors.senderClabe ? true : undefined}
                                    onChange={(e) => {
                                        onChange({
                                            ...rows,
                                            spei: {
                                                ...rows.spei,
                                                senderClabe: sanitize(e.target.value, /\D/g, 18),
                                            },
                                        });
                                    }}
                                    className={cn(RECEIPT_INPUT, 'w-[190px] font-mono')}
                                />
                            </div>
                        )
                    }
                    footer={
                        readOnly
                            ? null
                            : [speiErrors.speiKey, speiErrors.senderClabe]
                                  .filter((msg): msg is string => !!msg)
                                  .map((msg) => (
                                      <p key={msg} className={ERROR_TEXT}>
                                          {msg}
                                      </p>
                                  ))
                    }
                />
            )}

            {cardModalOpen && (
                <CardSplitsModal
                    rows={rows}
                    totalCents={totalCents}
                    onChange={onChange}
                    readOnly={readOnly}
                    onClose={() => {
                        setCardModalOpen(false);
                    }}
                />
            )}
        </div>
    );
}

/** Con una tarjeta abre su comprobante; con varias dice cuántas. Abre el reparto entre tarjetas. */
function CardDetailButton({
    rows,
    readOnly,
    hasErrors,
    onOpen,
}: {
    rows: FixedPaymentRows;
    readOnly: boolean;
    hasErrors: boolean;
    onOpen: () => void;
}) {
    const count = rows.card.cards.length;
    const authCode = count === 1 ? rows.card.cards[0].authCode : '';
    const hasAuth = authCode !== '';
    if (readOnly && count === 1 && !hasAuth) return null;
    const label =
        count > 1
            ? `${String(count)} tarjetas`
            : hasAuth
              ? `Autorización ${authCode}`
              : 'Autorización';
    return (
        <button
            type="button"
            onClick={onOpen}
            className={cn(
                'inline-flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-[12.5px] font-semibold transition-colors',
                hasErrors
                    ? 'border-destructive/50 bg-destructive/5 text-destructive'
                    : hasAuth || count > 1
                      ? 'border-primary/40 bg-primary/5 text-foreground hover:bg-primary/10'
                      : 'border-dashed border-border bg-card text-muted-foreground hover:border-primary/40 hover:text-foreground',
            )}
        >
            {hasAuth && !hasErrors && (
                <Check className="size-3.5 flex-none text-primary" aria-hidden />
            )}
            {label}
        </button>
    );
}

function MethodRow({
    icon,
    title,
    active,
    locked,
    readOnly,
    amountDisabled,
    cents,
    onCommit,
    onToggleLock,
    onAssignAll,
    detail,
    footer,
}: {
    icon: ReactNode;
    title: string;
    active: boolean;
    locked: boolean;
    readOnly: boolean;
    amountDisabled: boolean;
    cents: number;
    onCommit: (cents: number) => void;
    onToggleLock: () => void;
    onAssignAll: () => void;
    /** Captura del medio; solo se ve con el renglón activo. */
    detail: ReactNode;
    footer?: ReactNode;
}) {
    return (
        <div
            role="group"
            aria-label={title}
            className={cn(
                'flex flex-col gap-1.5 rounded-[10px] border p-2.5 transition-colors',
                active
                    ? 'border-primary/40 bg-card shadow-[0_0_0_1px_hsl(var(--primary)/0.05)]'
                    : 'border-border bg-muted/20',
            )}
        >
            <div className="flex flex-wrap items-center gap-2 md:flex-nowrap">
                <span
                    className={cn(
                        'flex size-10 flex-none items-center justify-center rounded-[10px] transition-colors',
                        active
                            ? 'bg-primary text-primary-foreground'
                            : 'bg-muted text-foreground/60',
                    )}
                >
                    {icon}
                </span>
                <div
                    className={cn(
                        'w-[96px] flex-none text-[13.5px] font-semibold',
                        !active && 'text-muted-foreground',
                    )}
                >
                    {title}
                </div>
                <div className="flex min-w-0 flex-1 items-center gap-2">
                    {active ? detail : <span aria-hidden />}
                </div>
                <RowAmount
                    cents={cents}
                    label={`Monto de ${title}`}
                    readOnly={readOnly}
                    disabled={amountDisabled}
                    onCommit={onCommit}
                />
                {!readOnly && (
                    <>
                        <IconButton
                            label={
                                locked
                                    ? `Desbloquear monto de ${title}`
                                    : `Bloquear monto de ${title}`
                            }
                            pressed={locked}
                            onClick={onToggleLock}
                        >
                            {locked ? (
                                <Lock className="size-4" aria-hidden />
                            ) : (
                                <Unlock className="size-4" aria-hidden />
                            )}
                        </IconButton>
                        <IconButton label={`Asignar todo a ${title}`} onClick={onAssignAll}>
                            <Zap className="size-4" aria-hidden />
                        </IconButton>
                    </>
                )}
            </div>
            {active && footer}
        </div>
    );
}
